import { MessageShortListItems } from "../message/messageShortListItems";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { AbortDictionary, getApiCall, postApiCall } from "./tokenService";
import { MessagePage } from "../message/messagePage";
import { MessagePageData } from "../message/messagePageData";
import { UnitNodePermissions } from "../unit/unitNodePermissions";
import { ModuleUnits } from "../unit/moduleUnits";
import { MessageTypeListItems } from "../messageType/messageTypeListItems";
import { MessageTypeEdit } from "../messageType/messageTypeEdit";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { CompanyGroupPermissions } from "../companyGroup/companyGroupPermissions";
import { MessageListItems } from "../message/messageListItems";
import { MessageEdit } from "../message/messageEdit";
import { ReadReceiptListItems } from "ui/Scripts/source/models/readReceipt/readReceiptListItems";
import { SaveSuccessItems } from "ui/Scripts/source/models/common/saveSuccessItems";
import { MessageSendEnmailListItems } from "../messageSendEnmail/messageSendEnmailListItems";
import { UnitModuleSaveResult } from "../unit/unitModuleSaveResult";

//Abort
const frontAborts = new AbortDictionary();
const reportAborts = new AbortDictionary();
const chartAborts = new AbortDictionary();

// #region FrontPage
export const getMessageShortListItems = (companyId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, dateRange: number): Promise<MessageShortListItems> => {
    const operationId = "getMessageShortListItems";
    frontAborts.abort(operationId);
    return postApiCall("api/message/listshort", MessageShortListItems, JSON.stringify({
        companyId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || "",
        dateRange
    }), (abortId: string) => {
        frontAborts.add(operationId, abortId);
    }).finally(() => {
        frontAborts.remove(operationId);
    });
};

export const sendReadReceipt = (id: string, companyId: string, category: number): Promise<ApiSuccess> => {
    return postApiCall("api/message/sendreadreceipt?id=" + id + "&companyId=" + companyId + "&category=" + category.toString(10), ApiSuccess);
};
// #endregion FrontPage

// #region MessagePage
export const getMessagePage = (): Promise<MessagePage> => {
    return getApiCall("api/message", MessagePage);
};

export const getMessagePageData = (companyId: string, editMode: boolean): Promise<MessagePageData> => {
    return getApiCall("api/message/pagedata?id=" + companyId + "&editMode=" + editMode.toString(), MessagePageData);
};

export const getMessageListItems = (companyId: string, pageSize: number, page: number, editMode: boolean, filter: string, startDate: number, endDate: number, dynamicTime: number,
    companyIds: string[], unitIds: string[], modifyUserCodes: string[], sortColumn: string, sortOrderIsAsc: boolean): Promise<MessageListItems> => {
    reportAborts.abortAll();
    chartAborts.abortAll();
    return postApiCall("api/message/list", MessageListItems, JSON.stringify({
        companyId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        editMode,
        startDate: startDate || null,
        endDate: endDate || null,
        dynamicTime,
        filter: filter || "",
        companyIds,
        unitIds,
        modifyUserCodes
    }));
};
// #endregion MessagePage

// #region MessageAttachments
export const messageEditRemoveAttachments = (data: any): Promise<ApiSuccess> => {
    return postApiCall("api/message/editremoveattachments", ApiSuccess, JSON.stringify(data));
};

export const messageEditAddAttachments = (data: any): Promise<SaveSuccessItems> => {
    return postApiCall("api/message/editaddattachments", SaveSuccessItems, JSON.stringify(data));
};
// #endregion MessageAttachments

// #region MessageReadReceipts
export const getMessageReadReceiptListItems = (companyId: string, messageId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<ReadReceiptListItems> => {
    return postApiCall("api/message/readreceipt/list", ReadReceiptListItems, JSON.stringify({
        companyId,
        messageId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || ""
    }));
};
// #endregion MessageReadReceipts

// #region MessageSendEnmails
export const getMessageSendEnmailListItems = (companyId: string, messageId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<MessageSendEnmailListItems> => {
    return postApiCall("api/message/sendemail/list", MessageSendEnmailListItems, JSON.stringify({
        companyId,
        messageId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || ""
    }));
};
// #endregion MessageSendEnmails

// #region Module Permissions
export const getUnitNodePermissions = (companyId: string): Promise<UnitNodePermissions> => {
    return getApiCall("api/message/module/permissions?id=" + companyId, UnitNodePermissions);
};

export const saveUnitNodePermissions = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/message/module/savepermissions", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module Permissions

// #region Module Company Group Permissions
export const getCompanyGroupPermissions = (companyId: string): Promise<CompanyGroupPermissions> => {
    return getApiCall("api/message/module/companygrouppermissions?id=" + companyId, CompanyGroupPermissions);
};

export const saveCompanyGroupPermissions = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/message/module/savecompanygrouppermissions", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module Company Group Permissions

// #region Module units
export const getUnits = (companyId: string): Promise<ModuleUnits> => {
    return getApiCall("api/message/module/units?id=" + companyId, ModuleUnits);
};

export const saveUnits = (obj: any): Promise<UnitModuleSaveResult> => {
    return postApiCall("api/message/module/saveunits", UnitModuleSaveResult, JSON.stringify(obj));
};
// #endregion Module units

// #region Module message types
export const getMessageTypeListItems = (companyId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<MessageTypeListItems> => {
    return postApiCall("api/message/module/messagetype/list", MessageTypeListItems, JSON.stringify({
        companyId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || ""
    }));
};

export const getMessageTypeEdit = (companyId: string, id: string): Promise<MessageTypeEdit> => {
    return getApiCall("api/message/module/messagetype/edit?id=" + id + "&companyId=" + companyId, MessageTypeEdit);
};

export const saveMessageTypeEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/message/module/messagetype/save", SaveSuccess, JSON.stringify(obj));
};

export const removeMessageTypes = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/message/module/messagetype/remove", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module message types

// #region Edit
export const getMessageEdit = (companyId: string, messageId: string, forwardMessage: boolean): Promise<MessageEdit> => {
    return getApiCall("api/message/edit?id=" + messageId + "&companyId=" + companyId + "&forwardMessage=" + forwardMessage.toString(), MessageEdit);
};

export const saveMessageEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/message/editsave", SaveSuccess, JSON.stringify(obj));
};

export const removeMessage = (companyId: string, messageId: string): Promise<ApiSuccess> => {
    return postApiCall("api/message/remove?id=" + messageId + "&companyId=" + companyId, ApiSuccess);
};
// #endregion Edit
